import React, { useCallback, useRef } from 'react';
import { buildFigureRegions, FIGURE_VIEWBOX } from './geometry';

const COLORS = {
  fill: '#E5E7EB',
  stroke: '#9CA3AF',
  selectedFill: '#EF4444',
  selectedStroke: '#B91C1C',
  editStroke: '#3B82F6',
  activeStroke: '#1D4ED8',
};

function renderShape(shape, commonProps) {
  // Optional rigid rotation about a pivot (used to angle the arms outward).
  const transform = shape.rotate
    ? `rotate(${shape.rotate.deg} ${shape.rotate.cx} ${shape.rotate.cy})`
    : undefined;
  const props = transform ? { transform, ...commonProps } : commonProps;
  switch (shape.type) {
    case 'circle':
      return <circle cx={shape.cx} cy={shape.cy} r={shape.r} {...props} />;
    case 'rect':
      return (
        <rect
          x={shape.x}
          y={shape.y}
          width={shape.w}
          height={shape.h}
          rx={shape.rx ?? 0}
          ry={shape.ry ?? shape.rx ?? 0}
          {...props}
        />
      );
    case 'ellipse':
      return <ellipse cx={shape.cx} cy={shape.cy} rx={shape.rx} ry={shape.ry} {...props} />;
    case 'polygon':
      return <polygon points={shape.points} {...props} />;
    default:
      return null;
  }
}

// Returns a new shape with its position translated by (dx, dy) in SVG units.
function translateShape(shape, dx, dy) {
  switch (shape.type) {
    case 'circle':
    case 'ellipse':
      return { ...shape, cx: shape.cx + dx, cy: shape.cy + dy };
    case 'rect':
      return { ...shape, x: shape.x + dx, y: shape.y + dy };
    case 'polygon': {
      const points = shape.points
        .split(/\s+/)
        .map((pair) => {
          const [px, py] = pair.split(',').map(Number);
          return `${px + dx},${py + dy}`;
        })
        .join(' ');
      return { ...shape, points };
    }
    default:
      return shape;
  }
}

export default function BodyMapFigure({
  view,
  title,
  selectedSlugs,
  onToggle,
  disabled = false,
  editMode = false,
  overrides,
  activeSlug,
  onActiveSlugChange,
  onOverrideChange,
}) {
  const regions = buildFigureRegions(view, overrides);
  const svgRef = useRef(null);
  const dragStateRef = useRef(null);

  const clientToSvg = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * FIGURE_VIEWBOX.width,
      y: ((clientY - rect.top) / rect.height) * FIGURE_VIEWBOX.height,
    };
  }, []);

  const handlePointerDown = (e, slug, shape) => {
    if (!editMode) return;
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    onActiveSlugChange?.(slug);
    const start = clientToSvg(e.clientX, e.clientY);
    dragStateRef.current = { slug, shape, startSvg: start };
  };

  const handlePointerMove = (e) => {
    const drag = dragStateRef.current;
    if (!drag) return;
    const current = clientToSvg(e.clientX, e.clientY);
    const dx = current.x - drag.startSvg.x;
    const dy = current.y - drag.startSvg.y;
    const moved = translateShape(drag.shape, dx, dy);
    // Strip the type field — overrides only need the geometric params, since
    // the renderer takes type from the default shape.
    const { type, ...geomOverride } = moved;
    onOverrideChange?.(drag.slug, geomOverride);
  };

  const handlePointerUp = (e) => {
    const drag = dragStateRef.current;
    if (!drag) return;
    e.currentTarget?.releasePointerCapture?.(e.pointerId);
    dragStateRef.current = null;
  };

  return (
    <div className="flex flex-col items-center">
      <div className="text-sm font-medium text-gray-700 mb-2">{title}</div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${FIGURE_VIEWBOX.width} ${FIGURE_VIEWBOX.height}`}
        width={FIGURE_VIEWBOX.width}
        height={FIGURE_VIEWBOX.height}
        role="group"
        aria-label={`${title} body map`}
        // Individual region drag disabled — Edit Layout now only repositions
        // the Selected Areas panel.
        // onPointerMove={editMode ? handlePointerMove : undefined}
        // onPointerUp={editMode ? handlePointerUp : undefined}
        // onPointerLeave={editMode ? handlePointerUp : undefined}
      >
        {regions.map(({ slug, shape }) => {
          const isSelected = selectedSlugs?.has(slug);
          // Edit-mode "move indicator" styling (blue/dashed strokes, thicker
          // border, active highlight) disabled along with per-region drag.
          // const isActive = editMode && activeSlug === slug;
          // const stroke = editMode
          //   ? isActive
          //     ? COLORS.activeStroke
          //     : COLORS.editStroke
          //   : isSelected
          //     ? COLORS.selectedStroke
          //     : COLORS.stroke;
          const stroke = isSelected ? COLORS.selectedStroke : COLORS.stroke;
          const fill = isSelected ? COLORS.selectedFill : COLORS.fill;

          return renderShape(shape, {
            key: slug,
            'data-region': slug,
            fill,
            stroke,
            strokeWidth: 1,
            // strokeDasharray: editMode && !isActive ? '3 2' : undefined,
            style: {
              cursor: disabled ? 'default' : 'pointer',
              transition: 'fill 120ms ease',
              touchAction: 'auto',
            },
            // Individual region drag disabled.
            // onPointerDown: editMode ? (e) => handlePointerDown(e, slug, shape) : undefined,
            onClick:
              disabled || editMode
                ? undefined
                : () => onToggle?.(slug),
            tabIndex: disabled || editMode ? -1 : 0,
            onKeyDown:
              disabled || editMode
                ? undefined
                : (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onToggle?.(slug);
                    }
                  },
          });
        })}
      </svg>
    </div>
  );
}
